"use client";

import { type ReactNode, useId } from "react";
import type { LayoutGroup, LayoutSummary } from "./layout-grouping";
import "./layout-groups.css";

export function LayoutGroupCard<T extends LayoutSummary>({
  group,
  renderLayout,
  initiallyExpanded = false,
}: {
  group: LayoutGroup<T>;
  renderLayout: (
    layout: T,
    role: "main" | "sub",
    roleLabelId: string,
  ) => ReactNode;
  initiallyExpanded?: boolean;
}) {
  const labelId = useId();
  const hasSubs = group.subs.length > 0;
  return (
    <div className={`layout-group-card${hasSubs ? " has-subs" : ""}`}>
      <div className="layout-group-stack">
        {hasSubs && (
          <>
            <span
              className="layout-group-sheet layout-group-sheet-back"
              aria-hidden="true"
            />
            <span
              className="layout-group-sheet layout-group-sheet-front"
              aria-hidden="true"
            />
          </>
        )}
        <div className="layout-group-main">
          <span className="layout-group-badge" id={`${labelId}-main`}>
            メイン配置図
          </span>
          <div className="layout-group-content">
            {renderLayout(group.main, "main", `${labelId}-main`)}
          </div>
        </div>
      </div>
      {hasSubs && (
        <details
          className="layout-group-subs"
          open={initiallyExpanded}
          aria-label={`${group.main.title}のサブ配置図`}
        >
          <summary
            aria-label={`${group.main.title}のサブ配置図 ${group.subs.length}枚`}
          >
            <span className="layout-group-summary-show">
              サブ配置図 {group.subs.length}枚を表示
            </span>
            <span className="layout-group-summary-hide">
              サブ配置図 {group.subs.length}枚を閉じる
            </span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="m6 9 6 6 6-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </summary>
          <ul className="layout-group-sub-list">
            {group.subs.map((layout, index) => (
              <li className="layout-group-sub" key={layout.id}>
                <span
                  className="layout-group-badge is-sub"
                  id={`${labelId}-${layout.id}`}
                >
                  サブ配置図 {index + 1}
                </span>
                <div className="layout-group-content">
                  {renderLayout(layout, "sub", `${labelId}-${layout.id}`)}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
